import { useCallback, useMemo } from "react";
import { Button, Flex, Icon, Icons, type TableColumnType } from "@ir/ui";
import {
  MasterDataProductUom,
  type IMasterDataProductUom,
  type MasterDataProductUom as MasterDataProductUomEntity,
} from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  MasterDataListTable,
  createMasterDataLinkedSelectionColumn,
  formatMasterDataCellText,
  renderMasterDataLinkedNameCell,
} from "./masterDataTable.shared";

const PRODUCT_UOM_LINKED_TOOLTIP =
  "Can't delete this UOM because it's linked to a product.";

export interface MasterDataProductUomRow extends IMasterDataProductUom {
  key: string;
}

function mapUomsToRows(
  productUoms: MasterDataProductUomEntity[],
): MasterDataProductUomRow[] {
  return productUoms.map((entity) => ({
    ...MasterDataProductUom.toJSON(entity),
    key: String(entity.uomId),
  }));
}

interface BuildColumnsArgs {
  onEdit: (productUom: MasterDataProductUomEntity) => void;
  productUomsById: Map<number, MasterDataProductUomEntity>;
}

function buildColumns({
  onEdit,
  productUomsById,
}: BuildColumnsArgs): TableColumnType<MasterDataProductUomRow>[] {
  return [
    {
      title: "UOM Name",
      dataIndex: "uomName",
      key: "uomName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 220,
      fixed: "left",
      cellRender: (value: unknown, record: MasterDataProductUomRow) =>
        renderMasterDataLinkedNameCell(value, record, PRODUCT_UOM_LINKED_TOOLTIP),
    },
    {
      title: "UOM Code",
      dataIndex: "uomCode",
      key: "uomCode",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 140,
    },
    {
      title: "UOM Description",
      dataIndex: "uomDescription",
      key: "uomDescription",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 360,
      cellRender: (value: unknown) => formatMasterDataCellText(value),
    },
    {
      title: "Actions",
      dataIndex: "uomId",
      key: "actions",
      align: "center",
      fixed: "right",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 80,
      cellRender: (value: unknown) => {
        const id = Number(value);
        const productUom = productUomsById.get(id);
        if (!productUom) return null;
        return (
          <HasAnyPermission requiredPermissions={[Permissions.MASTER_DATA_EDIT]}>
            <Flex align="center" justifyContent="center">
              <Button
                size="small"
                variant="outline"
                color="grey"
                aria-label={`Edit ${productUom.uomName}`}
                onPress={() => onEdit(productUom)}
              >
                <Icon name={Icons.Edit} size="small" />
              </Button>
            </Flex>
          </HasAnyPermission>
        );
      },
    },
  ];
}

interface MasterDataProductUomsTableProps {
  productUoms: MasterDataProductUomEntity[];
  search: string;
  setSearch: (search: string) => void;
  onEdit: (productUom: MasterDataProductUomEntity) => void;
  showSelection: boolean;
  selectedIds: ReadonlySet<number>;
  onSelectionChange: (selectedIds: Set<number>) => void;
}

export const MasterDataProductUomsTable = ({
  productUoms,
  search,
  setSearch,
  onEdit,
  showSelection,
  selectedIds,
  onSelectionChange,
}: MasterDataProductUomsTableProps) => {
  const productUomsById = useMemo(() => {
    const map = new Map<number, MasterDataProductUomEntity>();
    for (const productUom of productUoms) {
      map.set(productUom.uomId, productUom);
    }
    return map;
  }, [productUoms]);

  const dataSource = useMemo(() => mapUomsToRows(productUoms), [productUoms]);

  const selectedRowKeys = useMemo(
    () => Array.from(selectedIds).map(String),
    [selectedIds],
  );

  const selectableKeys = useMemo(
    () =>
      productUoms
        .filter((productUom) => !productUom.isLinked)
        .map((productUom) => String(productUom.uomId)),
    [productUoms],
  );

  const handleSelectionChange = useCallback(
    (keys: Set<string>) => {
      const next = new Set<number>();
      for (const key of keys) {
        const id = Number(key);
        const productUom = productUomsById.get(id);
        if (productUom && !productUom.isLinked) {
          next.add(id);
        }
      }
      onSelectionChange(next);
    },
    [onSelectionChange, productUomsById],
  );

  const columns = useMemo(() => {
    const dataColumns = buildColumns({ onEdit, productUomsById });
    if (!showSelection) return dataColumns;
    return [
      createMasterDataLinkedSelectionColumn<MasterDataProductUomRow>({
        selectedKeys: new Set(selectedRowKeys),
        selectableKeys,
        onChange: handleSelectionChange,
        getAriaLabel: (record) =>
          record.isLinked
            ? `${record.uomName} is linked to a product and cannot be selected`
            : `Select ${record.uomName}`,
      }),
      ...dataColumns,
    ];
  }, [
    handleSelectionChange,
    onEdit,
    productUomsById,
    selectableKeys,
    selectedRowKeys,
    showSelection,
  ]);

  return (
    <MasterDataListTable
      columns={columns}
      dataSource={dataSource}
      search={search}
      entityLabel="product UOMs"
      ariaLabel="Product UOM table"
      setSearch={setSearch}
      setPage={() => {}}
    />
  );
};
